import { enumToOptions } from "@repo/helpers";
import {
	Checkbox,
	DateTimeRangePicker,
	DropDownList,
	filterSpanTwoClassName,
	FiltersV2,
	Loading,
	TextBox,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useState } from "react";

import type { EnrollmentValidationFilter } from "./types";

import EnrollmentValidationApi from "../../api/enrollmentValidationApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { RepsDropdown } from "../../components/repsDropdown";
import { SegmentFilters } from "../../components/segmentFilters";
import { yesNoOptions } from "../../constants/constants";
import { RepIncidentAssignedStatus, RepIncidentStatus } from "../../models/complaint";
import { EnrollmentValidationGrid } from "./grid";
import * as STRINGS from "./strings";

const initialFilters: EnrollmentValidationFilter = {
	currentPage: 1,
	itemsPerPage: 10,
	startDate: startOfDay(new Date()),
	endDate: endOfDay(new Date()),
	assignedStatus: "",
	qaAnalystId: null,
	validationTypeIds: [],
	status: "",
	isValid: "",
	enrollmentId: "",
	saleId: "",
	accountNumber: "",
	customerPhone: "",
	customerName: "",
	hasCustomerIncident: undefined,
	branchIds: [],
	repIds: [],
};

export function EnrollmentValidationPage() {
	const [draftFilters, setDraftFilters] = useState<EnrollmentValidationFilter>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<EnrollmentValidationFilter>(initialFilters);
	//Only controls which reps show in the Representatives dropdown - not part of the grid filters
	const [activeRepsOnly, setActiveRepsOnly] = useState(true);

	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Users],
		queryFn: () => EnrollmentValidationApi.getUsers(),
	});

	const validationTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Types],
		queryFn: () => EnrollmentValidationApi.getTypes(),
	});

	const filterChanged = (name: string, value: any) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		<Loading loading={false}>
			<main>
				<FiltersV2
					onApply={() =>
						setAppliedFilters((currentFilters) => ({
							...draftFilters,
							currentPage: 1,
							itemsPerPage: currentFilters.itemsPerPage,
						}))
					}
				>
					<DateTimeRangePicker
						dateOnly
						id={STRINGS.INCIDENT_DATE}
						label="Date of Error"
						className={filterSpanTwoClassName}
						quickRange
						quickRangeOptions={["today", "yesterday", "month", "year", "all"]}
						value={[draftFilters.startDate, draftFilters.endDate]}
						setValue={(v: (Date | undefined)[]) =>
							setDraftFilters({ ...draftFilters, startDate: v[0], endDate: v[1] })
						}
						onQuickRangeSelected={(v) =>
							setAppliedFilters((currentFilters) => ({
								...draftFilters,
								startDate: v[0],
								endDate: v[1],
								currentPage: 1,
								itemsPerPage: currentFilters.itemsPerPage,
							}))
						}
					/>

					<SegmentFilters onChange={segmentFiltersChanged} />
					<RepsDropdown
						label="Representatives"
						value={draftFilters.repIds}
						changeHandler={(v) => filterChanged(STRINGS.REP_IDS, v)}
						filters={draftFilters}
						onlyActiveReps={activeRepsOnly}
					/>
					<Checkbox
						id={STRINGS.ACTIVE_REPS_ONLY}
						title="Active Reps Only"
						label=""
						value={activeRepsOnly}
						changeHandler={(v: boolean) => setActiveRepsOnly(v)}
						inline={false}
					/>

					<DropDownList
						id={STRINGS.ASSIGNED_STATUS}
						name={STRINGS.ASSIGNED_STATUS}
						label="Assigned Status"
						value={draftFilters.assignedStatus}
						changeHandler={(v) => filterChanged(STRINGS.ASSIGNED_STATUS, v)}
						fullLength
						clearable
						values={enumToOptions(RepIncidentAssignedStatus)}
					/>
					<DropDownList
						id={STRINGS.QA_ANALYST_ID}
						name={STRINGS.QA_ANALYST_ID}
						label="QA Analyst"
						value={draftFilters.qaAnalystId}
						changeHandler={(v) => filterChanged(STRINGS.QA_ANALYST_ID, v)}
						values={usersQuery.data ?? []}
						loaded={usersQuery.isFetched}
						clearable
						fullLength
						valueProp="Id"
						textProp="Name"
					/>
					<DropDownList
						id={STRINGS.VALIDATION_TYPE_IDS}
						name={STRINGS.VALIDATION_TYPE_IDS}
						label="Validation Type"
						value={draftFilters.validationTypeIds}
						changeHandler={(v) => filterChanged(STRINGS.VALIDATION_TYPE_IDS, v)}
						fullLength
						clearable
						loaded={validationTypesQuery.isFetched}
						values={validationTypesQuery.data ?? []}
						isMulti
						textProp="Name"
						valueProp="Id"
					/>

					<DropDownList
						id={STRINGS.STATUS}
						name={STRINGS.STATUS}
						label="Status"
						value={draftFilters.status}
						changeHandler={(v) => filterChanged(STRINGS.STATUS, v)}
						fullLength
						clearable
						values={enumToOptions(RepIncidentStatus)}
					/>

					<DropDownList
						id={STRINGS.IS_VALID}
						name={STRINGS.IS_VALID}
						label="Is Valid?"
						value={draftFilters.isValid}
						changeHandler={(v) => filterChanged(STRINGS.IS_VALID, v)}
						fullLength
						clearable
						values={yesNoOptions}
					/>

					<TextBox
						id={STRINGS.ENROLLMENT_ID}
						name={STRINGS.ENROLLMENT_ID}
						label="Enrollment Id"
						value={draftFilters.enrollmentId}
						changeHandler={(v) => filterChanged(STRINGS.ENROLLMENT_ID, v)}
					/>

					<TextBox
						id={STRINGS.SALE_ID}
						name={STRINGS.SALE_ID}
						label="Sale Id"
						value={draftFilters.saleId}
						changeHandler={(v) => filterChanged(STRINGS.SALE_ID, v)}
					/>

					<TextBox
						id={STRINGS.ACCOUNT_NUMBER}
						name={STRINGS.ACCOUNT_NUMBER}
						label="Account Number"
						value={draftFilters.accountNumber}
						changeHandler={(v) => filterChanged(STRINGS.ACCOUNT_NUMBER, v)}
					/>

					<TextBox
						id={STRINGS.CUSTOMER_PHONE}
						name={STRINGS.CUSTOMER_PHONE}
						label="Customer Phone #"
						value={draftFilters.customerPhone}
						changeHandler={(v) => filterChanged(STRINGS.CUSTOMER_PHONE, v)}
					/>

					<TextBox
						id={STRINGS.CUSTOMER_NAME}
						name={STRINGS.CUSTOMER_NAME}
						label="Customer Name"
						value={draftFilters.customerName}
						changeHandler={(v) => filterChanged(STRINGS.CUSTOMER_NAME, v)}
					/>

					<DropDownList
						id={STRINGS.HAS_CUSTOMER_INCIDENT}
						name={STRINGS.HAS_CUSTOMER_INCIDENT}
						label="Has Customer Incident"
						value={draftFilters.hasCustomerIncident}
						changeHandler={(v) => filterChanged(STRINGS.HAS_CUSTOMER_INCIDENT, v)}
						fullLength
						clearable
						values={yesNoOptions}
					/>
				</FiltersV2>
				<EnrollmentValidationGrid filters={appliedFilters} setFilters={setAppliedFilters} isAdmin={true} />
			</main>
		</Loading>
	);
}
